/**
 * PipelineVentas — vista principal del usuario ventas.
 *
 * Kanban full-screen que muestra todos los estados del flujo de pedidos.
 * Se adapta automáticamente a cada depósito:
 *   · R8      — flujo completo con asignación de operario
 *   · ISABELA  — sin asignación, estados simplificados + entregados desde pedidos_despachados
 *
 * Columnas con fondo coloreado = requieren acción de VENTAS (despachar, verificar error).
 */

import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  collection, onSnapshot, query,
  orderBy, where, limit,
  doc, updateDoc, serverTimestamp,
} from "firebase/firestore";
import { db } from "../../../firebase";
import { useAuth, hasRol } from "../../../context/AuthContext";
import { useApp } from "../../../context/AppContext";
import { revertirDespacho, marcarEntregadoAdmin } from "../services/pedidosFS";
import FacturadoBadge from "../components/FacturadoBadge";
import { useNavigate } from "react-router-dom";
import { syncPendientesDeHoy, syncRapida } from "../services/syncFinnegans";
import { FLUJO_ISABELA, FLUJO_R8 } from "../services/estadosConfig";
import { useDelegaciones } from "../hooks/useDelegaciones";
import { esPedidoComponentesExt, numerosComponentesReferenciados, kitSinVincular } from "../services/parseDescripcionPedido";
import ConfirmarDespacho from "./confirmarDespacho";
import AsignarDepositoModal from "../components/AsignarDepositoModal";
import { bucketFecha, BUCKET_ORDER } from "../services/agrupaciones";

// Aliases locales para mantener los nombres que usa este componente
const STATES_ISABELA = FLUJO_ISABELA;
const STATES_R8      = FLUJO_R8;

// ─────────────────────────────────────────────────────────────
//  Helpers
// ─────────────────────────────────────────────────────────────

const STUCK_MS = 2 * 24 * 3600 * 1000;

// Estados donde ventas puede reasignar el depósito (antes de que el operario prepare el pedido)
const ESTADOS_REASIGNABLES = new Set(["PENDIENTE_ASIGNAR", "ASIGNADO", "EN_PREPARACION", "CON_ERROR", "ERROR_CONFIRMADO"]);

function isStuck(p) {
  if (!p.updatedAt?.seconds) return false;
  const final = ["DESPACHADO", "CANCELADO", "ANULADO", "DESP_ENTREGADO"];
  if (final.includes(p.estado)) return false;
  return Date.now() - p.updatedAt.seconds * 1000 > STUCK_MS;
}

function formatTimeAgo(ts) {
  if (!ts) return null;
  const d = ts?.seconds ? new Date(ts.seconds * 1000) : (ts instanceof Date ? ts : new Date(ts));
  if (isNaN(d)) return null;
  const m = Math.floor((Date.now() - d.getTime()) / 60000);
  if (m < 1) return "ahora";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

function getPedidoDate(p) {
  if (p?.finFechaTS?.seconds) return new Date(p.finFechaTS.seconds * 1000);
  if (p?.finFecha) return new Date(`${p.finFecha}T00:00:00`);
  return null;
}

function toDate(ts) {
  if (!ts) return null;
  if (ts?.seconds) return new Date(ts.seconds * 1000);
  const d = ts instanceof Date ? ts : new Date(ts);
  return isNaN(d.getTime()) ? null : d;
}

// Fecha en que el pedido entró a la columna/estado (estadoId) en que se está mostrando
function getEstadoColDate(p, estadoId) {
  if (estadoId === "DESP_PENDIENTE" || estadoId === "DESPACHADO") {
    return toDate(p.despachadoAt) || toDate(p.timestamps?.DESPACHADO) || toDate(p.updatedAt) || getPedidoDate(p);
  }
  if (estadoId === "DESP_ENTREGADO" || estadoId === "ENTREGADO") {
    return toDate(p.entregadoAt) || toDate(p.updatedAt) || getPedidoDate(p);
  }
  return toDate(p.timestamps?.[estadoId]) || toDate(p.updatedAt) || getPedidoDate(p);
}

function formatFechaCorta(d) {
  if (!d) return null;
  return d.toLocaleDateString("es-AR", { day: "2-digit", month: "short" });
}

// Resuelve el nombre del responsable/operario asignado al pedido
function getResponsable(p) {
  return (
    p?.asignadoNombre ||
    p?.asignado?.nombre ||
    p?.asignado?.displayName ||
    p?.operarioNombre ||
    p?.responsableNombre ||
    p?.responsable?.nombre ||
    null
  );
}

// ─────────────────────────────────────────────────────────────
//  PipelineCard
// ─────────────────────────────────────────────────────────────

function PipelineCard({ p, cfg, onReasignar, pagosSet, onDespachar, onFacturar }) {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const { toast } = useApp();
  const [reverting, setReverting] = useState(false);
  const [entregandoAdmin, setEntregandoAdmin] = useState(false);
  const esAdmin = hasRol(profile, "admin");
  const stuck = isStuck(p);
  // Kit que no se pudo vincular a su pedido de componentes → alerta para Ventas.
  const kitAlerta = kitSinVincular(p);
  const items = p.productos?.length ?? p.items?.length ?? 0;
  const timeAgo = formatTimeAgo(p.timestamps?.[p.estado] || p.updatedAt);
  const creacion = getPedidoDate(p);
  const responsable = getResponsable(p);
  const puedeReasignar = onReasignar && ESTADOS_REASIGNABLES.has(p.estado);
  // El pago/cobranza es SOLO de ISABELA; en R8 no se muestra el badge de pagado.
  const isPagado = String(p.deposito || "").toUpperCase() === "ISABELA" && !!pagosSet?.has(p.id);

  const metodoColors = {
    AGENCIA: { bg: "#eff6ff", color: "#1d4ed8" },
    RETIRA:  { bg: "#f0fdf4", color: "#15803d" },
    CAMION:  { bg: "#fef3c7", color: "#92400e" },
    GIRA:    { bg: "#fdf4ff", color: "#7e22ce" },
  };
  const mc = metodoColors[p.metodoEntrega] || { bg: "#f1f5f9", color: "#475569" };

  // Ventas solo VE el detalle del pedido (no interactúa con la carga). El "ir a
  // Carga de Camión" es exclusivo del encargado (desde Gestión de Pedidos).
  const href = `/pedidos/${encodeURIComponent(p.id)}`;

  return (
    <div
      onClick={() => navigate(href)}
      style={{
        background: "#fff",
        border: stuck
          ? "2px solid #f97316"
          : `1px solid ${cfg.border}`,
        // Acento rojo a la izquierda cuando el kit no está vinculado (distinto del
        // naranja de "atascado" para no confundir los dos tipos de alerta).
        borderLeft: kitAlerta ? "4px solid #dc2626" : undefined,
        borderRadius: "10px",
        padding: "10px 12px",
        marginBottom: "7px",
        cursor: "pointer",
        boxShadow: stuck
          ? "0 2px 8px rgba(249,115,22,0.18)"
          : "0 1px 3px rgba(0,0,0,0.05)",
        position: "relative",
        transition: "transform 0.1s, box-shadow 0.1s",
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = "translateY(-1px)";
        e.currentTarget.style.boxShadow = stuck
          ? "0 4px 12px rgba(249,115,22,0.25)"
          : "0 4px 10px rgba(0,0,0,0.1)";
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = "";
        e.currentTarget.style.boxShadow = stuck
          ? "0 2px 8px rgba(249,115,22,0.18)"
          : "0 1px 3px rgba(0,0,0,0.05)";
      }}
    >
      {/* Badge atascado */}
      {stuck && (
        <div style={{
          position: "absolute", top: -9, right: 8,
          background: "#f97316", color: "#fff",
          fontSize: "0.56rem", fontWeight: 800,
          padding: "2px 7px", borderRadius: "999px",
          letterSpacing: "0.05em", textTransform: "uppercase",
        }}>
          +2 días
        </div>
      )}

      {/* Fila 1: # pedido + tiempo en estado */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "3px" }}>
        <span style={{ fontWeight: 800, fontSize: "0.82rem", color: "#0f172a" }}>
          #{p.numero || p.id}
        </span>
        {timeAgo && (
          <span style={{
            fontSize: "0.65rem",
            color: stuck ? "#f97316" : "#94a3b8",
            fontWeight: stuck ? 700 : 500,
          }}>
            ⏱ {timeAgo}
          </span>
        )}
      </div>

      {/* Fila 2: cliente */}
      <p style={{
        margin: "0 0 5px",
        fontSize: "0.85rem", fontWeight: 600, color: "#1e293b",
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        lineHeight: 1.3,
      }}>
        {p.cliente || "—"}
      </p>

      {/* Fila 3: ítems + método + fecha creación + depósito */}
      <div style={{ display: "flex", alignItems: "center", gap: "5px", flexWrap: "wrap" }}>
        {items > 0 && (
          <span style={{ fontSize: "0.67rem", color: "#64748b" }}>
            {items} ítem{items !== 1 ? "s" : ""}
          </span>
        )}
        {p.metodoEntrega && (
          <span style={{
            fontSize: "0.6rem", fontWeight: 700,
            background: mc.bg, color: mc.color,
            padding: "1px 6px", borderRadius: "999px",
          }}>
            {p.metodoEntrega}
          </span>
        )}
        {p.tieneModificacion && p.modificacionEstado !== "RESUELTA" && (
          <span style={{ fontSize: "0.6rem", fontWeight: 700, background: "#fffbeb", color: "#b45309", border: "1px solid #fde68a", padding: "1px 6px", borderRadius: "999px" }}>
            ✏️ Modificación
          </span>
        )}
        {isPagado && (
          <span style={{
            fontSize: "0.58rem", fontWeight: 700,
            background: "#f0fdf4", color: "#15803d",
            padding: "1px 6px", borderRadius: "999px",
            border: "1px solid #86efac",
          }}>
            💳 Pagado
          </span>
        )}
        {/* Responsable / operario asignado */}
        {responsable && (
          <span style={{
            fontSize: "0.6rem", color: "#64748b",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100px",
          }}>
            👤 {responsable}
          </span>
        )}
        {/* Fecha de creación (referencia) */}
        {creacion && p.estado !== "PENDIENTE_ASIGNAR" && (
          <span style={{ fontSize: "0.6rem", color: "#94a3b8", marginLeft: "auto" }}>
            📅 {formatFechaCorta(creacion)}
          </span>
        )}
        {/* Depósito — solo cuando se ven todos los depósitos */}
        {p.deposito && !p._hideDeposito && (
          <span style={{
            fontSize: "0.58rem", color: "#94a3b8",
            marginLeft: creacion ? "0" : "auto",
          }}>
            {p.deposito}
          </span>
        )}
      </div>

      {/* Alerta: kit sin vincular a su pedido de componentes. Tocar la card lleva al
          detalle, donde Ventas puede vincularlo a mano. */}
      {kitAlerta && (
        <div style={{
          marginTop: "7px", display: "flex", alignItems: "center", gap: "6px",
          background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "8px",
          padding: "5px 9px",
        }}>
          <span style={{ fontSize: "0.8rem", lineHeight: 1 }}>🧩</span>
          <span style={{ fontSize: "0.68rem", fontWeight: 800, color: "#b91c1c" }}>
            Kit sin vincular
          </span>
        </div>
      )}

      {/* Botón reasignar depósito — solo en estados pre-preparado */}
      {puedeReasignar && (
        <button
          onClick={e => { e.stopPropagation(); onReasignar(p); }}
          title="Cambiar depósito o método de entrega"
          style={{
            marginTop: "8px",
            width: "100%",
            padding: "5px 8px",
            border: "1px solid #e2e8f0",
            borderRadius: "6px",
            background: "#f8fafc",
            color: "#64748b",
            fontSize: "0.68rem",
            fontWeight: 600,
            cursor: "pointer",
            textAlign: "center",
            transition: "all 0.12s",
          }}
          onMouseEnter={e => { e.currentTarget.style.background = "#f1f5f9"; e.currentTarget.style.color = "#334155"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "#f8fafc"; e.currentTarget.style.color = "#64748b"; }}
        >
          ↕ Cambiar destino
        </button>
      )}

      {/* Estado de facturación (para despachados). */}
      {p.estado === "DESPACHADO" && (
        <div style={{ marginTop: "8px" }}><FacturadoBadge facturado={p.facturado} /></div>
      )}

      {/* Des-despachar (ventas): flechita roja compacta. Solo si no se cargó al
          camión todavía. Vuelve el pedido a "pendiente de despachar". */}
      {p.estado === "DESPACHADO" && p.cargaEstado !== "CARGADO" && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "6px" }}>
          <button
            onClick={async (e) => {
              e.stopPropagation();
              if (reverting) return;
              if (!window.confirm(`¿Deshacer el despacho de #${p.numero || p.id}? Vuelve a "pendiente de despachar".`)) return;
              setReverting(true);
              try {
                await revertirDespacho(p.id, { uid: user?.uid });
                toast?.success?.(`#${p.numero || p.id} volvió a pendiente de despachar`);
              } catch (err) {
                toast?.error?.(err?.message || "No se pudo des-despachar");
              } finally {
                setReverting(false);
              }
            }}
            title="Volver a pendiente de despachar (deshacer el despacho)"
            style={{
              width: "28px", height: "28px", padding: 0,
              border: "1px solid #fecaca", borderRadius: "8px",
              background: "#fef2f2", color: "#b91c1c",
              fontSize: "0.95rem", fontWeight: 800,
              cursor: reverting ? "default" : "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              lineHeight: 1,
            }}
          >
            {reverting ? "…" : "↩"}
          </button>
        </div>
      )}

      {/* Atajo Ventas: despachar sin entrar al detalle (abre modal de validación
          de método + agencia/viaje). 🔒 R8 solo desde CONTROLADO (no saltear control);
          ISABELA también desde PREPARADO (no tiene paso de control aparte). */}
      {onDespachar && (
        p.estado === "CONTROLADO" ||
        (p.estado === "PREPARADO" && String(p.deposito || "").toUpperCase() === "ISABELA")
      ) && (
        <button
          onClick={(e) => { e.stopPropagation(); onDespachar(p); }}
          style={{
            marginTop: "8px", width: "100%", padding: "9px",
            border: "none", borderRadius: "8px",
            background: "#0f172a", color: "#fff",
            fontSize: "0.82rem", fontWeight: 800, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
          }}
        >
          🚚 Despachar
        </button>
      )}

      {/* Administración (Marcela): estado "Por Facturar" → Facturar (principal) o
          Volver (compacto, ícono). onFacturar solo se pasa a la columna POR_FACTURAR.
          El botón Volver es icon-only para que ambos entren siempre en la card angosta. */}
      {onFacturar && (
        <div style={{ marginTop: "8px", display: "flex", gap: "6px", alignItems: "stretch" }}>
          <button
            onClick={(e) => { e.stopPropagation(); onFacturar(p); }}
            style={{
              flex: 1, minWidth: 0, padding: "9px", border: "none", borderRadius: "8px",
              background: "#b45309", color: "#fff", fontSize: "0.82rem", fontWeight: 800,
              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
            }}
          >
            🧾 Facturar
          </button>
          <button
            onClick={async (e) => {
              e.stopPropagation();
              if (reverting) return;
              if (!window.confirm(`¿Deshacer el despacho de #${p.numero || p.id}? Vuelve a "pendiente de despachar".`)) return;
              setReverting(true);
              try {
                await revertirDespacho(p.id, { uid: user?.uid });
                toast?.success?.(`#${p.numero || p.id} volvió a pendiente de despachar`);
              } catch (err) {
                toast?.error?.(err?.message || "No se pudo deshacer el despacho");
              } finally {
                setReverting(false);
              }
            }}
            title="Se despachó por error → volver a pendiente de despachar"
            aria-label="Volver a pendiente de despachar"
            style={{
              flexShrink: 0, width: "42px", padding: 0, border: "1px solid #fecaca", borderRadius: "8px",
              background: "#fef2f2", color: "#b91c1c", fontSize: "0.95rem", fontWeight: 800,
              cursor: reverting ? "default" : "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1,
            }}
          >
            {reverting ? "…" : "↩"}
          </button>
        </div>
      )}

      {/* Override de ADMIN: forzar ENTREGADO desde cualquier estado (sin control/escaneo). */}
      {esAdmin && !["ENTREGADO", "CANCELADO", "ANULADO", "DESP_ENTREGADO"].includes(p.estado) && (
        <button
          onClick={async (e) => {
            e.stopPropagation();
            if (entregandoAdmin) return;
            if (!window.confirm(`ADMIN: marcar #${p.numero || p.id} como ENTREGADO directo (sin control ni escaneo)?`)) return;
            setEntregandoAdmin(true);
            try {
              await marcarEntregadoAdmin(p.id, { uid: user?.uid, nombre: profile?.nombre || null });
              toast?.success?.(`#${p.numero || p.id} marcado como entregado (admin)`);
            } catch (err) {
              toast?.error?.(err?.message || "No se pudo marcar entregado");
            } finally {
              setEntregandoAdmin(false);
            }
          }}
          title="ADMIN: marcar como entregado directo (sin control ni escaneo)"
          style={{
            marginTop: "8px", width: "100%", padding: "5px 8px",
            border: "1px dashed #6366f1", borderRadius: "6px",
            background: "#eef2ff", color: "#4f46e5",
            fontSize: "0.68rem", fontWeight: 700,
            cursor: entregandoAdmin ? "default" : "pointer", textAlign: "center",
          }}
        >
          {entregandoAdmin ? "Marcando…" : "⚙️ Marcar entregado (admin)"}
        </button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  PipelineColumn
// ─────────────────────────────────────────────────────────────

function PipelineColumn({ cfg, items, colHeight, onReasignar, pagosSet, labelOverride, groupedInner, onDespachar, onFacturar }) {
  const stuckCount = items.filter(isStuck).length;

  // Agrupar por bucket de fecha (HOY/AYER/...) según cuándo entraron a este estado
  const porFecha = {};
  for (const p of items) {
    const bucket = bucketFecha(getEstadoColDate(p, cfg.id));
    (porFecha[bucket] ||= []).push(p);
  }
  const bucketsConItems = BUCKET_ORDER.filter(b => (porFecha[b] || []).length > 0);

  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      background: "#fff",
      borderRadius: groupedInner ? "10px" : "14px",
      border: groupedInner
        ? "1px solid #cbd5e1"
        : (cfg.ventasAction ? `2px solid ${cfg.border}` : "1.5px solid #e2e8f0"),
      overflow: "hidden",
      boxShadow: (!groupedInner && cfg.ventasAction)
        ? `0 0 0 3px ${cfg.border}40`
        : "none",
      /* Columna crece para ocupar todo el alto disponible */
      minHeight: groupedInner ? "100px" : "120px",
      height: colHeight,
    }}>

      {/* Cabecera de columna */}
      <div style={{
        background: cfg.ventasAction ? cfg.headerBg : "#f8fafc",
        borderBottom: `1px solid ${cfg.ventasAction ? cfg.border : "#f1f5f9"}`,
        padding: "11px 13px 10px",
        flexShrink: 0,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: cfg.ventasAction || cfg.sublabel ? "4px" : 0 }}>
          <span style={{ fontSize: "0.88rem", lineHeight: 1 }}>{cfg.icon}</span>
          <span style={{
            flex: 1,
            fontWeight: 700,
            fontSize: "0.78rem",
            color: cfg.ventasAction ? cfg.color : "#475569",
            letterSpacing: "-0.01em",
          }}>
            {labelOverride || cfg.label}
          </span>
          {/* Badge atascados en columna */}
          {stuckCount > 0 && (
            <span style={{
              background: "#f97316", color: "#fff",
              fontSize: "0.58rem", fontWeight: 800,
              padding: "1px 5px", borderRadius: "999px",
            }}>
              ⚠{stuckCount}
            </span>
          )}
          {/* Contador de pedidos */}
          <span style={{
            background: items.length > 0
              ? cfg.color + "22"
              : "#f1f5f9",
            color: items.length > 0 ? cfg.color : "#94a3b8",
            fontSize: "0.72rem", fontWeight: 700,
            padding: "1px 8px", borderRadius: "999px",
            minWidth: "22px", textAlign: "center",
          }}>
            {items.length}
          </span>
        </div>
        {/* Sub-label para columnas con acción de ventas */}
        {cfg.sublabel && (
          <p style={{
            margin: 0,
            fontSize: "0.6rem",
            fontWeight: 700,
            color: cfg.ventasAction ? cfg.color : "#64748b",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}>
            {cfg.ventasAction ? "▶ " : ""}{cfg.sublabel}
          </p>
        )}
      </div>

      {/* Cuerpo scrolleable */}
      <div style={{
        flex: 1,
        overflowY: "auto",
        padding: "9px",
        background: items.length === 0 ? "#fafafa" : "transparent",
      }}>
        {items.length === 0 ? (
          <p style={{
            textAlign: "center",
            color: "#d1d5db",
            fontSize: "0.75rem",
            padding: "16px 0",
            margin: 0,
          }}>
            Sin pedidos
          </p>
        ) : (
          bucketsConItems.map(bucket => (
            <div key={bucket} style={{ marginBottom: "8px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "6px", padding: "2px 0 5px" }}>
                <span style={{ fontSize: "0.6rem", fontWeight: 700, color: cfg.color, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  {bucket}
                </span>
                <div style={{ flex: 1, height: "1px", background: cfg.border || "#e2e8f0" }} />
                <span style={{ fontSize: "0.6rem", fontWeight: 700, color: cfg.color }}>
                  {porFecha[bucket].length}
                </span>
              </div>
              {porFecha[bucket].map(p => (
                <PipelineCard key={p.id} p={p} cfg={cfg} onReasignar={onReasignar} pagosSet={pagosSet} onDespachar={onDespachar} onFacturar={onFacturar} />
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  Componente principal
// ─────────────────────────────────────────────────────────────

export default function PipelineVentas() {
  const { user, profile } = useAuth();
  const { toast } = useApp();
  const navigate = useNavigate();

  // Si el usuario tiene un depósito asignado en su perfil, lo usamos como filtro inicial
  // Normalizamos a UPPERCASE para que coincida con los chips de depósito
  const depositoInicial = profile?.deposito ? String(profile.deposito).trim().toUpperCase() : "";

  const [pedidos, setPedidos]               = useState([]);
  const [despachadosAll, setDespachadosAll] = useState([]);
  const [entregadosAll, setEntregadosAll]   = useState([]);
  const [pagosPendientes, setPagosPendientes] = useState(0);
  const [pagosSet,      setPagosSet]        = useState(new Set());
  const [sinDeposito,   setSinDeposito]     = useState(0);
  const [loading, setLoading]               = useState(true);
  const [searchQ, setSearchQ]               = useState("");
  const [filterDeposito, setFilterDeposito] = useState(depositoInicial);
  // Proceso (solo R8): separa los dos flujos de entrega —
  //   "retiro"      = RETIRA (el cliente retira en depósito; entrega directa)
  //   "transporte"  = AGENCIA / GIRA / CAMIÓN (se cargan al camión)
  const [procesoTab, setProcesoTab]         = useState("transporte");
  // Sub-filtro dentro de Transporte: "todos" | "AGENCIA" | "GIRA" | "CAMION".
  const [transporteSub, setTransporteSub]   = useState("todos");
  // Filtro GLOBAL de método de entrega (solo R8). A diferencia del split
  // Retiro/Transporte (que separa solo post-despacho), este filtra TODO el pipeline
  // (preparación + entrega). "TODOS" = sin filtro (comportamiento por defecto).
  const [metodoGlobal, setMetodoGlobal]     = useState("TODOS");
  // Cuando metodoGlobal === "GIRA": id de la gira puntual, "" = todas las giras.
  const [filterGiraId, setFilterGiraId]     = useState("");
  // Giras activas (no cerradas y con fecha futura) para el selector del filtro.
  const [giras, setGiras]                   = useState([]);
  const [reasignando, setReasignando]       = useState(null); // pedido a reasignar
  const [despachando, setDespachando]       = useState(null); // pedido a despachar (atajo ventas)
  const [isMobile, setIsMobile]             = useState(() => window.innerWidth < 768);

  // ── Detectar mobile/desktop (resizable) ──
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  // ── Detección de rol ──
  // Chequeamos ambos campos para cubrir perfiles con role/rol mixtos
  const _roleField = (profile?.role || "").toLowerCase();
  const _rolField  = (profile?.rol  || "").toLowerCase();
  const _role      = _roleField || _rolField;
  // Administración (Marcela): ve el pipeline pero solo actúa en "Por Facturar".
  const esAdministracion = hasRol(profile, "administracion") && !(_role === "admin");
  // Delegación "Sin Administración": cuando Marcela está de licencia, el admin activa
  // config/app.delegaciones.ventasFactura y VENTAS (y admin) toman la facturación.
  const deleg = useDelegaciones();
  // puedeFacturar: quién ve la columna "Por Facturar" y puede marcar facturado.
  //   · Administración (Marcela) SIEMPRE.
  //   · Ventas/Admin SOLO cuando la delegación ventasFactura está activa.
  // Ventas NO queda en soloLectura: sigue despachando/reasignando, solo suma Facturar.
  const puedeFacturar = esAdministracion
    || ((_role === "ventas" || _role === "admin") && !!deleg.ventasFactura);
  // soloLectura: visor puede ver todo pero no actuar. Administración también es
  // "solo lectura" para el resto del pipeline (su única acción es Facturar/Volver).
  const soloLectura = _roleField === "visor" || _rolField === "visor" || esAdministracion;
  // puedeReasignarDeposito: solo ventas y admin pueden cambiar el destino de un pedido
  const puedeReasignarDeposito = (_role === "ventas" || _role === "admin") && !soloLectura;
  // Modo "Sin Ventas": cuando el admin activa administracionDespacha, Marcela cubre a
  // ventas y puede despachar — SOLO R8 (ISABELA implicaría registrar el pago, fuera de
  // alcance). Backstop server-side: la regla de create solo la deja despachar R8 CONTROLADO.
  const marcelaDespacha = esAdministracion && !!deleg.administracionDespacha;
  // Atajo de despacho: ventas/admin sin límite; Marcela delegada solo pedidos R8.
  // Visor y Marcela-sin-delegación: null (no despachan).
  const onDespacharAtajo = !soloLectura
    ? setDespachando
    : (marcelaDespacha
        ? (p) => {
            if (String(p.deposito || "").toUpperCase() === "R8") setDespachando(p);
            else toast?.error?.("En modo Sin Ventas solo podés despachar pedidos de R8.");
          }
        : null);

  // Facturar: marca facturado=true en el MISMO doc de pedidos_despachados (no mueve
  // colección). El pedido pasa de "Por Facturar" a "Por cargar"/"Para entregar".
  // Lo hace Marcela (esAdministracion) o Ventas/Admin con la delegación activa.
  const onFacturar = puedeFacturar ? async (p) => {
    try {
      await updateDoc(doc(db, p._col || "pedidos_despachados", p.id), {
        facturado: true,
        facturadoAt: serverTimestamp(),
        facturadoPor: user?.uid || null,
      });
      toast?.success?.(`#${p.numero || p.id} facturado ✓`);
    } catch (e) {
      console.error("[pipeline] facturar", e);
      toast?.error?.("No se pudo facturar");
    }
  } : null;
  const [syncing, setSyncing]               = useState(false);
  const [lastSync, setLastSync]             = useState(null);
  const syncRunRef                          = useRef(false);

  // ── Auto-sync al entrar a la página (una vez por sesión) ──
  useEffect(() => {
    if (syncRunRef.current) return;
    syncRunRef.current = true;
    (async () => {
      setSyncing(true);
      try { await syncPendientesDeHoy(); setLastSync(new Date()); }
      catch (e) { console.error("[PipelineVentas] sync auto error:", e); }
      finally { setSyncing(false); }
    })();
  }, []);

  async function handleSync() {
    if (syncing) return;
    setSyncing(true);
    try {
      await syncRapida(); // función del servidor (batcheada) con fallback client-side
      setLastSync(new Date());
      toast.success("Pedidos actualizados ✓");
    } catch (e) {
      console.error(e);
      toast.error("No se pudieron sincronizar los pedidos");
    } finally {
      setSyncing(false);
    }
  }

  // Medir el TopBar para calcular la altura disponible exacta
  const [topbarH, setTopbarH] = useState(60);
  useEffect(() => {
    const measure = () => {
      const tb = document.querySelector(".topbar");
      if (tb) setTopbarH(tb.offsetHeight);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  // ── Listener pedidos activos (colección principal) ──
  // Cuando hay un depósito seleccionado usamos where("deposito", ==) igual que el
  // encargado — esto garantiza reactividad en tiempo real sin depender de índices
  // compuestos ni de que updatedAt ya esté confirmado por el servidor.
  // Cuando se ve "Todos" usamos orderBy + limit como antes.
  useEffect(() => {
    setLoading(true);
    let q;
    if (filterDeposito) {
      q = query(
        collection(db, "pedidos"),
        where("deposito", "==", filterDeposito)
      );
    } else {
      q = query(
        collection(db, "pedidos"),
        orderBy("updatedAt", "desc"),
        limit(500)
      );
    }
    const unsub = onSnapshot(q,
      snap => {
        setPedidos(
          snap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .filter(p => !["CANCELADO", "ANULADO"].includes(p.estado))
        );
        setLoading(false);
      },
      err => { console.error("[PipelineVentas]", err); setLoading(false); }
    );
    return () => unsub();
  }, [filterDeposito]); // ← reactivo: se re-subscribe cuando cambia el depósito

  // ── Listener pedidos_despachados ──
  // Se usan DOS listeners:
  //   1. ISABELA: sin filtro de fecha — muestra TODOS los pendientes de entrega sin importar cuándo se despacharon
  //   2. R8: últimos 30 días — solo para la columna "Despachados hoy"
  useEffect(() => {
    // Listener ISABELA — sin restricción de fecha, todos los despachados de ese depósito
    // Sin orderBy para evitar requerir índice compuesto; el orden se aplica en JS (byEstado)
    const qIsabela = query(
      collection(db, "pedidos_despachados"),
      where("deposito", "==", "ISABELA"),
      limit(500)
    );

    // Listener R8 — últimos 30 días
    const since = new Date();
    since.setDate(since.getDate() - 30);
    since.setHours(0, 0, 0, 0);
    const qR8 = query(
      collection(db, "pedidos_despachados"),
      where("despachadoAt", ">=", since),
      orderBy("despachadoAt", "desc"),
      limit(300)
    );

    // Mantenemos dos Sets para combinarlos sin duplicados
    let isabelaDocs = [];
    let r8Docs      = [];

    function merge() {
      const seen = new Set();
      const combined = [];
      for (const d of [...isabelaDocs, ...r8Docs]) {
        if (!seen.has(d.id)) { seen.add(d.id); combined.push(d); }
      }
      setDespachadosAll(combined);
    }

    const unsubIsabela = onSnapshot(qIsabela,
      snap => { isabelaDocs = snap.docs.map(d => ({ id: d.id, ...d.data() })); merge(); },
      err  => console.error("[PipelineVentas] despachos ISABELA:", err)
    );
    const unsubR8 = onSnapshot(qR8,
      snap => { r8Docs = snap.docs.map(d => ({ id: d.id, ...d.data() })); merge(); },
      err  => console.error("[PipelineVentas] despachos R8:", err)
    );

    return () => { unsubIsabela(); unsubR8(); };
  }, []);

  // ── Listener pedidos_entregados — solo HOY para el pipeline ──
  // El histórico completo está en /ventas/entregados
  useEffect(() => {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const q = query(
      collection(db, "pedidos_entregados"),
      where("entregadoAt", ">=", hoy),
      limit(300)
    );
    const unsub = onSnapshot(q,
      snap => setEntregadosAll(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      err  => console.error("[PipelineVentas] entregados:", err)
    );
    return () => unsub();
  }, []);

  // ── Listener pagos — contador de pendientes + Set de pedidosId con pago ──
  // Solo para ventas/admin (respeta la regla de Firestore). Se espera a que el perfil esté cargado.
  useEffect(() => {
    const rf = (profile?.role || "").toLowerCase();
    const rl = (profile?.rol  || "").toLowerCase();
    const role = rf || rl;
    const isVisorUser = rf === "visor" || rl === "visor";
    if (!role || (!isVisorUser && role !== "ventas" && role !== "admin")) return;

    // Escuchamos TODOS los pagos para construir el Set de pedidoIds pagados
    const q = query(collection(db, "pagos"));
    const unsub = onSnapshot(q,
      snap => {
        let pendientes = 0;
        const ids = new Set();
        snap.forEach(d => {
          ids.add(d.id); // el doc id ES el pedidoId
          if (d.data().pagoEstado === "PENDIENTE") pendientes++;
        });
        setPagosSet(ids);
        setPagosPendientes(pendientes);
      },
      err  => console.error("[PipelineVentas] pagos:", err)
    );
    return () => unsub();
  }, [profile?.role, profile?.rol]);

  // ── Listener: conteo de pedidos sin depósito asignado ──
  useEffect(() => {
    const EXCLUIDOS = ["CANCELADO", "ANULADO", "DESPACHADO", "ENTREGADO"];
    let nullCount = 0, emptyCount = 0;
    function update() {
      setSinDeposito(nullCount + emptyCount);
    }
    const qNull = query(collection(db, "pedidos"), where("deposito", "==", null), limit(500));
    const qEmpty = query(collection(db, "pedidos"), where("deposito", "==", ""), limit(500));
    const unsubNull = onSnapshot(qNull,
      snap => { nullCount  = snap.docs.filter(d => !EXCLUIDOS.includes(d.data().estado)).length; update(); },
      err  => console.error("[PipelineVentas] sinDeposito null:", err)
    );
    const unsubEmpty = onSnapshot(qEmpty,
      snap => { emptyCount = snap.docs.filter(d => !EXCLUIDOS.includes(d.data().estado)).length; update(); },
      err  => console.error("[PipelineVentas] sinDeposito empty:", err)
    );
    return () => { unsubNull(); unsubEmpty(); };
  }, []);

  // ── Depósitos disponibles ──
  // Base fija para que los chips siempre aparezcan aunque no haya pedidos activos.
  // Se complementa con cualquier depósito extra que aparezca en Firestore.
  const DEPOSITOS_BASE = ["ISABELA", "R8"];
  const deposits = useMemo(() => {
    const fromPedidos = pedidos
      .map(p => p.deposito ? String(p.deposito).trim().toUpperCase() : null)
      .filter(Boolean);
    return [...new Set([...DEPOSITOS_BASE, ...fromPedidos])].sort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pedidos]);

  // ── Giras activas (solo R8) para el filtro global por gira ──
  // Solo las que NO pasaron todavía (fechaSalida >= hoy) y no están cerradas.
  // Las sin fecha se incluyen (no se puede saber si pasaron). Ordenadas por salida.
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "camiones"), (snap) => {
      const hoy0 = new Date(); hoy0.setHours(0, 0, 0, 0);
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(c => (c.tipo || "CAMION") === "GIRA")
        .filter(c => c.estado !== "CERRADO")
        .filter(c => !filterDeposito || String(c.deposito || "") === filterDeposito)
        .filter(c => {
          const ms = c.fechaSalida?.seconds ? c.fechaSalida.seconds * 1000
            : (typeof c.fechaSalida?.toMillis === "function" ? c.fechaSalida.toMillis() : 0);
          return ms === 0 || ms >= hoy0.getTime();
        })
        .sort((a, b) => (a.fechaSalida?.seconds || 0) - (b.fechaSalida?.seconds || 0));
      setGiras(list);
    }, () => setGiras([]));
    return () => unsub();
  }, [filterDeposito]);

  // ── ¿Estamos viendo ISABELA? ──
  const isIsabela = filterDeposito.toUpperCase() === "ISABELA";

  // ── Filtro GLOBAL de método (solo R8): "foco" que filtra TODO el pipeline ──
  // Cuando está activo (≠ TODOS) toma el control del layout de entrega (deriva el
  // proceso/sub efectivos) y oculta el split manual Retiro/Transporte. ISABELA no aplica.
  const metodoActivo = !isIsabela && metodoGlobal !== "TODOS";
  const procesoEf = metodoActivo ? (metodoGlobal === "RETIRA" ? "retiro" : "transporte") : procesoTab;
  const subEf     = metodoActivo ? (metodoGlobal === "RETIRA" ? "todos" : metodoGlobal) : transporteSub;
  // Predicado del filtro global: método + (si es GIRA con una gira elegida) camionId.
  // Cuando no está activo devuelve true → no altera nada del comportamiento actual.
  const pasaMetodoGlobal = useCallback((p) => {
    if (!metodoActivo) return true;
    if (String(p.deposito || "").toUpperCase() === "ISABELA") return true;
    const met = String(p.metodoEntrega || "").toUpperCase();
    if (met !== metodoGlobal) return false;
    if (metodoGlobal === "GIRA" && filterGiraId) return p.camionId === filterGiraId;
    return true;
  }, [metodoActivo, metodoGlobal, filterGiraId]);

  // Config de estados según depósito seleccionado.
  // Para visor (soloLectura) quitamos los colores de acción — no tiene nada que hacer.
  const stateConfig = useMemo(() => {
    let base;
    if (isIsabela) {
      base = STATES_ISABELA;
    } else {
      // R8 — columnas achicadas para VENTAS (no ve ASIGNADO ni CON_ERROR sin
      // revisar; el despachado se separa por método: entregar vs cargar camión).
      const byId = Object.fromEntries(STATES_R8.map(c => [c.id, c]));
      const desp = byId["DESPACHADO"] || {};
      // Columnas de preparación (comunes a ambos procesos).
      const prep = [
        byId["PENDIENTE_ASIGNAR"],
        byId["EN_PREPARACION"],
        byId["ERROR_CONFIRMADO"],
        byId["PREPARADO"],
        byId["CONTROLADO"],
      ];
      // Columnas de ENTREGA según el proceso elegido:
      //  · retiro     → Despachado (para entregar) → Entregado
      //  · transporte → Por cargar → En el camión → Entregado
      const entrega = procesoEf === "retiro"
        ? [
            { ...desp, id: "DESPACHADO", label: "Despachado · para entregar", sublabel: "Retira", icon: "🤝", color: "#15803d", bg: "#f0fdf4", border: "#86efac", headerBg: "#dcfce7" },
          ]
        : [
            { ...desp, id: "DESPACHADO_CARGA", label: "Por cargar", sublabel: "Agencia / gira / camión", icon: "📦", color: "#1d4ed8", bg: "#eff6ff", border: "#bfdbfe", headerBg: "#dbeafe" },
            { ...desp, id: "EN_CAMION", label: "En el camión", sublabel: "Cargado", icon: "🚛", color: "#0369a1", bg: "#f0f9ff", border: "#bae6fd", headerBg: "#e0f2fe" },
          ];
      // Columna extra "Por Facturar" (despachados sin facturar) ENTRE la preparación
      // y la entrega. Resaltada en ámbar. La ve Administración (Marcela) o, con la
      // delegación "Sin Administración" activa, Ventas/Admin.
      const porFacturar = puedeFacturar
        ? [{ ...desp, id: "POR_FACTURAR", label: "Por Facturar", sublabel: "Despachado · sin facturar", icon: "🧾", color: "#b45309", bg: "#fffbeb", border: "#fbbf24", headerBg: "#fef3c7" }]
        : [];
      base = [...prep, ...porFacturar, ...entrega, byId["ENTREGADO"]].filter(Boolean);
    }
    if (!soloLectura) return base;
    return base.map(cfg => ({ ...cfg, ventasAction: false }));
  }, [isIsabela, soloLectura, procesoEf, puedeFacturar]);

  // ── Familia de proceso (solo R8): retiro vs transporte + sub-método ──
  // Para ISABELA no aplica (tiene su propio flujo) → deja pasar todo.
  const perteneceFamilia = useCallback((p) => {
    // ISABELA no usa el split de procesos de R8 → siempre pasa.
    if (String(p.deposito || "").toUpperCase() === "ISABELA") return true;
    const met = String(p.metodoEntrega || "").toUpperCase();
    if (procesoEf === "retiro") return met === "RETIRA";
    // transporte: todo lo que NO es retira (agencia/gira/camión y sin clasificar)
    if (met === "RETIRA") return false;
    if (subEf !== "todos") return met === subEf;
    return true;
  }, [procesoEf, subEf]);

  // ── Filtro base de pedidos ──
  const pedidosFiltrados = useMemo(() => {
    const nq = searchQ.trim().toLowerCase();
    const refComp = numerosComponentesReferenciados(pedidos);
    return pedidos.filter(p => {
      // Excluir pedidos de componentes de kit: por descripción ("COMPONENTES KIT")
      // o porque un kit los referencia con "REF. N" en sus productos.
      if (esPedidoComponentesExt(p, refComp)) return false;
      // Filtro por depósito: comparación case-insensitive y trim para evitar falsos negativos
      if (filterDeposito) {
        const dep = String(p.deposito || "").trim().toUpperCase();
        if (dep !== filterDeposito.trim().toUpperCase()) return false;
      }
      // El split Retiro/Transporte NO filtra la preparación (los usuarios ven SIEMPRE
      // todos los pedidos que ingresan). PERO el filtro GLOBAL de método sí llega acá:
      // cuando está activo (≠ TODOS) filtra también la preparación.
      if (!pasaMetodoGlobal(p)) return false;
      if (nq) {
        const num = String(p.numero || p.id || "").toLowerCase();
        const cli = String(p.cliente || "").toLowerCase();
        return num.includes(nq) || cli.includes(nq);
      }
      return true;
    }).map(p => ({ ...p, _hideDeposito: !!filterDeposito }));
  }, [pedidos, searchQ, filterDeposito, pasaMetodoGlobal]);

  // ── Filtro base de despachados ──
  const despsFiltradas = useMemo(() => {
    const nq = searchQ.trim().toLowerCase();
    return despachadosAll.filter(p => {
      if (filterDeposito) {
        const dep = String(p.deposito || "").trim().toUpperCase();
        if (dep !== filterDeposito.trim().toUpperCase()) return false;
      }
      if (!perteneceFamilia(p)) return false;
      if (!pasaMetodoGlobal(p)) return false;
      if (nq) {
        const num = String(p.numero || p.id || "").toLowerCase();
        const cli = String(p.cliente || "").toLowerCase();
        return num.includes(nq) || cli.includes(nq);
      }
      return true;
    });
  }, [despachadosAll, searchQ, filterDeposito, perteneceFamilia, pasaMetodoGlobal]);

  // Despachados SIN el split de método (deposito + búsqueda). Fuente de la columna
  // "Por Facturar", que muestra TODOS los métodos. El filtro GLOBAL sí aplica acá
  // (si está activo) para que "Por Facturar" también respete el foco elegido.
  const despsTodoMetodo = useMemo(() => {
    const nq = searchQ.trim().toLowerCase();
    return despachadosAll.filter(p => {
      if (filterDeposito) {
        const dep = String(p.deposito || "").trim().toUpperCase();
        if (dep !== filterDeposito.trim().toUpperCase()) return false;
      }
      if (!pasaMetodoGlobal(p)) return false;
      if (nq) {
        const num = String(p.numero || p.id || "").toLowerCase();
        const cli = String(p.cliente || "").toLowerCase();
        return num.includes(nq) || cli.includes(nq);
      }
      return true;
    });
  }, [despachadosAll, searchQ, filterDeposito, pasaMetodoGlobal]);

  // ── Set de números de pedido que siguen activos en 'pedidos' (no terminados) ──
  // Evita que un pedido aparezca a la vez en 'Para despachar' y en las columnas
  // de pedidos_despachados cuando la operación de despacho quedó incompleta.
  const ESTADOS_TERMINADOS = new Set(["DESPACHADO", "ANULADO", "CANCELADO"]);
  const activosNums = useMemo(() => {
    const s = new Set();
    for (const p of pedidos) {
      if (!ESTADOS_TERMINADOS.has(p.estado)) {
        const num = String(p.numero || p.id || "").trim().toLowerCase();
        if (num) s.add(num);
      }
    }
    return s;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pedidos]);

  // Filtra despsFiltradas excluyendo pedidos que todavía están activos en 'pedidos'
  const despsNoActivos = useMemo(() =>
    despsFiltradas.filter(p => {
      const num = String(p.numero || p.id || "").trim().toLowerCase();
      return !activosNums.has(num);
    })
  , [despsFiltradas, activosNums]);

  // ── Pedidos de hoy (para columna DESPACHADO de R8) ──
  const despachosHoy = useMemo(() => {
    const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
    return despsNoActivos.filter(p => {
      if (!p.despachadoAt?.seconds) return false;
      return new Date(p.despachadoAt.seconds * 1000) >= hoy;
    }).map(p => ({ ...p, estado: "DESPACHADO", _hideDeposito: !!filterDeposito }));
  }, [despsNoActivos, filterDeposito]);

  // ── Virtuales ISABELA ──
  const isabelaPendienteEntrega = useMemo(() =>
    despsNoActivos
      .filter(p => p.necesitaControl !== false)
      .map(p => ({ ...p, estado: "DESP_PENDIENTE", _hideDeposito: !!filterDeposito }))
  , [despsNoActivos, filterDeposito]);

  // ── Helper: filtra entregadosAll por depósito, búsqueda y fecha (solo HOY) ──
  const entregadosFiltrados = useMemo(() => {
    const nq = searchQ.trim().toLowerCase();
    const hoyMs = (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d.getTime(); })();

    return entregadosAll.filter(p => {
      // Sólo pedidos entregados HOY — si no tiene fecha válida de hoy, no aparece en pipeline
      const ts = p.entregadoAt?.seconds
        ? p.entregadoAt.seconds * 1000
        : p.entregadoAt instanceof Date
          ? p.entregadoAt.getTime()
          : typeof p.entregadoAt === "number"
            ? p.entregadoAt
            : null;
      if (ts === null || ts < hoyMs) return false;

      if (filterDeposito) {
        const dep = String(p.deposito || "").trim().toUpperCase();
        if (dep !== filterDeposito.trim().toUpperCase()) return false;
      }
      if (!perteneceFamilia(p)) return false;
      if (!pasaMetodoGlobal(p)) return false;
      if (nq) {
        const num = String(p.numero || p.id || "").toLowerCase();
        const cli = String(p.cliente || "").toLowerCase();
        return num.includes(nq) || cli.includes(nq);
      }
      return true;
    });
  }, [entregadosAll, searchQ, filterDeposito, perteneceFamilia, pasaMetodoGlobal]);

  // ISABELA → estado virtual "DESP_ENTREGADO"
  const isabelaEntregados = useMemo(() =>
    entregadosFiltrados.map(p => ({ ...p, estado: "DESP_ENTREGADO", _hideDeposito: !!filterDeposito }))
  , [entregadosFiltrados, filterDeposito]);

  // R8 → estado virtual "ENTREGADO"
  const r8Entregados = useMemo(() =>
    entregadosFiltrados.map(p => ({ ...p, estado: "ENTREGADO", _hideDeposito: !!filterDeposito }))
  , [entregadosFiltrados, filterDeposito]);

  // ── Agrupación por estado → columna ──
  const byEstado = useMemo(() => {
    const out = {};
    for (const cfg of stateConfig) out[cfg.id] = [];

    for (const p of pedidosFiltrados) {
      let e = p.estado || "PENDIENTE_ASIGNAR";
      // Ventas: ASIGNADO se muestra dentro de "En preparación" (no tiene columna propia).
      if (!isIsabela && e === "ASIGNADO") e = "EN_PREPARACION";
      if (out[e] !== undefined) out[e].push(p);
    }

    // Columnas derivadas de pedidos_despachados / pedidos_entregados
    if (isIsabela) {
      // ISABELA — DESP_PENDIENTE: pedidos_despachados + fallback desde colección pedidos
      const despFromPedidos = pedidosFiltrados
        .filter(p => p.estado === "DESPACHADO")
        .map(p => ({ ...p, estado: "DESP_PENDIENTE", _hideDeposito: !!filterDeposito }));
      const idsFromDespachos = new Set(isabelaPendienteEntrega.map(p => p.id));
      out["DESP_PENDIENTE"] = [
        ...isabelaPendienteEntrega,
        ...despFromPedidos.filter(p => !idsFromDespachos.has(p.id)),
      ];
      out["DESP_ENTREGADO"] = isabelaEntregados;
    } else {
      // R8 — DESPACHADO: pendientes de su entrega/carga (necesitaControl !== false),
      // excluyendo los ya cargados (CARGADO) o entregados (ENTREGADO).
      // Se SEPARA por método: RETIRA → entregar; agencia/camión → cargar camión.
      const r8Pend = despsNoActivos
        .filter(p => p.necesitaControl !== false && p.cargaEstado !== "CARGADO" && p.cargaEstado !== "ENTREGADO");
      // El fallback de despachados desde 'pedidos' SÍ se filtra por familia
      // (es post-despacho), a diferencia de las columnas de preparación.
      const despFromPedidosR8 = pedidosFiltrados
        .filter(p => p.estado === "DESPACHADO" && perteneceFamilia(p));
      const idsPend = new Set(r8Pend.map(p => p.id));
      const todosDesp = [...r8Pend, ...despFromPedidosR8.filter(p => !idsPend.has(p.id))];

      const esRetira = p => p.metodoEntrega === "RETIRA";
      // Con facturación activa (Marcela, o Ventas/Admin delegados), las columnas de
      // despacho muestran SOLO los facturados; los NO facturados van a "Por Facturar"
      // (partición limpia por `facturado`, sin duplicar). Sin delegación, no cambia nada.
      const facturadoOk = (p) => !puedeFacturar || p.facturado === true;
      out["DESPACHADO"] = todosDesp
        .filter(p => esRetira(p) && facturadoOk(p))
        .map(p => ({ ...p, estado: "DESPACHADO", _hideDeposito: !!filterDeposito }));
      out["DESPACHADO_CARGA"] = todosDesp
        .filter(p => !esRetira(p) && facturadoOk(p))
        .map(p => ({ ...p, estado: "DESPACHADO_CARGA", _hideDeposito: !!filterDeposito }));

      // "Por Facturar": despachados SIN facturar, TODOS los métodos (sin el filtro
      // Retiro/Transporte). La ve quien tiene la facturación (Marcela o Ventas/Admin
      // delegados). Son los mismos que sin delegación aparecen en "Por cargar/Despachado".
      if (puedeFacturar) {
        const activos = activosNums;
        out["POR_FACTURAR"] = despsTodoMetodo
          .filter(p => !activos.has(String(p.numero || p.id || "").trim().toLowerCase()))
          .filter(p => p.necesitaControl !== false && p.cargaEstado !== "CARGADO" && p.cargaEstado !== "ENTREGADO" && p.facturado !== true)
          .map(p => ({ ...p, estado: "POR_FACTURAR", _hideDeposito: !!filterDeposito }));
      }
      // R8 — EN_CAMION: agencia/camión ya cargados (cargaEstado CARGADO) pero aún no
      // entregados. Como por ahora "En el camión" es el ÚLTIMO estado de estos pedidos,
      // mostramos SOLO los cargados HOY (los de días anteriores ya salieron; cuando
      // esté el escaneo del camionero pasarán a Entregado).
      const _hoy0 = new Date(); _hoy0.setHours(0, 0, 0, 0);
      const _iniHoy = _hoy0.getTime();
      const _finHoy = _iniHoy + 24 * 60 * 60 * 1000;
      const cargadoHoy = (p) => {
        const ms = p.cargadoAt?.seconds ? p.cargadoAt.seconds * 1000
          : (typeof p.cargadoAt?.toMillis === "function" ? p.cargadoAt.toMillis() : 0);
        return ms >= _iniHoy && ms < _finHoy;
      };
      out["EN_CAMION"] = despsNoActivos
        .filter(p => p.cargaEstado === "CARGADO" && p.metodoEntrega !== "RETIRA" && cargadoHoy(p))
        .map(p => ({ ...p, estado: "EN_CAMION", _hideDeposito: !!filterDeposito }));
      // R8 — ENTREGADO: desde pedidos_entregados
      out["ENTREGADO"] = r8Entregados;
    }

    // Ordenar cada columna: atascados primero, luego por fecha de ingreso al estado (desc)
    for (const key of Object.keys(out)) {
      out[key].sort((a, b) => {
        if (isStuck(a) !== isStuck(b)) return isStuck(a) ? -1 : 1;
        return (getEstadoColDate(b, key)?.getTime() ?? 0) - (getEstadoColDate(a, key)?.getTime() ?? 0);
      });
    }

    return out;
  }, [pedidosFiltrados, stateConfig, isIsabela, isabelaPendienteEntrega, isabelaEntregados, r8Entregados, despsNoActivos, despachosHoy, filterDeposito, perteneceFamilia, puedeFacturar, despsTodoMetodo, activosNums]);

  // ── Totales para el header ──
  const totalPedidos = pedidosFiltrados.length;
  const totalAtascados = pedidosFiltrados.filter(isStuck).length;
  const totalKitsSinVincular = pedidosFiltrados.filter(kitSinVincular).length;
  const totalAccion = useMemo(() =>
    stateConfig
      .filter(c => c.ventasAction)
      .reduce((acc, c) => acc + (byEstado[c.id]?.length || 0), 0)
  , [stateConfig, byEstado]);

  // ── Altura disponible: viewport - TopBar (medido dinámicamente) ──
  // Pipeline header interno: 58px | Grid padding top+bottom: 20px
  const availH    = `calc(100vh - ${topbarH}px)`;
  const colHeight = `calc(100vh - ${topbarH}px - 58px - 20px)`;

  // ── Unidades de render: DESPACHADO + DESPACHADO_CARGA se agrupan bajo "Despachado" ──
  const pipelineUnits = useMemo(() => {
    const units = [];
    const cargar = stateConfig.find(c => c.id === "DESPACHADO_CARGA");
    const entregar = stateConfig.find(c => c.id === "DESPACHADO");
    const puedeAgrupar = !!cargar && !!entregar; // solo R8
    let groupInserted = false;
    for (const cfg of stateConfig) {
      if (puedeAgrupar && (cfg.id === "DESPACHADO" || cfg.id === "DESPACHADO_CARGA")) {
        if (!groupInserted) { units.push({ type: "group", cargar, entregar }); groupInserted = true; }
        continue;
      }
      units.push({ type: "single", cfg });
    }
    return units;
  }, [stateConfig]);

  // ── Render de una sección mobile (reutilizable para single y sub-columnas del grupo) ──
  function renderMobileSection(cfg, { labelOverride, forceShow } = {}) {
    const items = byEstado[cfg.id] || [];
    if (items.length === 0 && !cfg.ventasAction && !forceShow) return null;
    const porFechaMobile = {};
    for (const p of items) {
      const bucket = bucketFecha(getEstadoColDate(p, cfg.id));
      (porFechaMobile[bucket] ||= []).push(p);
    }
    const bucketsConItemsMobile = BUCKET_ORDER.filter(b => (porFechaMobile[b] || []).length > 0);
    return (
      <div key={cfg.id} style={{ marginBottom: "16px" }}>
        <div style={{
          display: "flex", alignItems: "center", gap: "6px",
          padding: "7px 10px",
          background: cfg.ventasAction ? cfg.headerBg : "#f8fafc",
          border: `1px solid ${cfg.ventasAction ? cfg.border : "#e2e8f0"}`,
          borderRadius: items.length > 0 ? "10px 10px 0 0" : "10px",
          borderBottom: items.length > 0 ? "none" : undefined,
        }}>
          <span style={{ fontSize: "0.85rem", lineHeight: 1 }}>{cfg.icon}</span>
          <span style={{ flex: 1, fontWeight: 700, fontSize: "0.78rem", color: cfg.ventasAction ? cfg.color : "#475569" }}>
            {labelOverride || cfg.label}
          </span>
          <span style={{
            background: items.length > 0 ? cfg.color + "22" : "#f1f5f9",
            color: items.length > 0 ? cfg.color : "#94a3b8",
            fontSize: "0.7rem", fontWeight: 700, padding: "1px 8px", borderRadius: "999px",
          }}>
            {items.length}
          </span>
        </div>
        {items.length > 0 ? (
          <div style={{
            border: `1px solid ${cfg.ventasAction ? cfg.border : "#e2e8f0"}`,
            borderTop: "none", borderRadius: "0 0 10px 10px",
            background: "#fff", padding: "8px",
            display: "flex", flexDirection: "column", gap: "6px",
          }}>
            {bucketsConItemsMobile.map(bucket => (
              <div key={bucket}>
                <div style={{ display: "flex", alignItems: "center", gap: "6px", padding: "2px 0 5px" }}>
                  <span style={{ fontSize: "0.6rem", fontWeight: 700, color: cfg.color, textTransform: "uppercase", letterSpacing: "0.06em" }}>{bucket}</span>
                  <div style={{ flex: 1, height: "1px", background: cfg.border || "#e2e8f0" }} />
                  <span style={{ fontSize: "0.6rem", fontWeight: 700, color: cfg.color }}>{porFechaMobile[bucket].length}</span>
                </div>
                {porFechaMobile[bucket].map(p => (
                  <PipelineCard key={p.id} p={p} cfg={cfg} onReasignar={puedeReasignarDeposito ? setReasignando : null} pagosSet={pagosSet} onDespachar={onDespacharAtajo} onFacturar={cfg.id === "POR_FACTURAR" ? onFacturar : null} />
                ))}
              </div>
            ))}
          </div>
        ) : forceShow ? (
          <div style={{ border: "1px solid #e2e8f0", borderTop: "none", borderRadius: "0 0 10px 10px", background: "#fafafa", padding: "10px", textAlign: "center", color: "#cbd5e1", fontSize: "0.75rem" }}>
            Sin pedidos
          </div>
        ) : null}
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────
  //  Render
  // ─────────────────────────────────────────────────────────────

  return (
    <div style={{
      background: "#f1f5f9",
      height: availH,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    }}>

      {/* ── Header compacto ── */}
      <div style={{
        background: "#fff",
        borderBottom: "1px solid #e2e8f0",
        padding: "0 20px",
        height: "58px",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        gap: "14px",
        zIndex: 10,
      }}>

        {/* Volver al menú principal */}
        <a
          href="/inicio"
          title="Volver al menú"
          style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "34px", height: "34px", flexShrink: 0, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "10px", textDecoration: "none", fontSize: "1rem", color: "#334155" }}
        >
          ←
        </a>

        {/* Título + badges */}
        <h1 style={{ margin: 0, fontSize: "1rem", fontWeight: 800, color: "#0f172a", whiteSpace: "nowrap" }}>
          Pedidos
        </h1>

        {/* Badge: pedidos con acción */}
        {!loading && totalAccion > 0 && (
          <span style={{
            background: "#15803d", color: "#fff",
            fontSize: "0.7rem", fontWeight: 800,
            padding: "3px 10px", borderRadius: "999px",
            whiteSpace: "nowrap", flexShrink: 0,
          }}>
            {totalAccion} para despachar
          </span>
        )}

        {/* Badge: atascados */}
        {!loading && totalAtascados > 0 && (
          <span style={{
            background: "#f97316", color: "#fff",
            fontSize: "0.7rem", fontWeight: 800,
            padding: "3px 10px", borderRadius: "999px",
            whiteSpace: "nowrap", flexShrink: 0,
          }}>
            ⚠️ {totalAtascados} atascado{totalAtascados !== 1 ? "s" : ""}
          </span>
        )}

        {/* Badge: kits sin vincular a su pedido de componentes */}
        {!loading && totalKitsSinVincular > 0 && (
          <span style={{
            background: "#dc2626", color: "#fff",
            fontSize: "0.7rem", fontWeight: 800,
            padding: "3px 10px", borderRadius: "999px",
            whiteSpace: "nowrap", flexShrink: 0,
          }}>
            🧩 {totalKitsSinVincular} kit{totalKitsSinVincular !== 1 ? "s" : ""} sin vincular
          </span>
        )}

        <div style={{ flex: 1 }} />

        {/* Filtro GLOBAL de método (solo R8): dropdown que filtra TODO el pipeline.
            Con "Gira" aparece un segundo dropdown para elegir una gira puntual. */}
        {!isIsabela && (
          <div style={{ display: "flex", alignItems: "center", gap: "6px", flexShrink: 0 }}>
            <span style={{ fontSize: "0.72rem", fontWeight: 700, color: "#64748b" }}>Método:</span>
            <select
              value={metodoGlobal}
              onChange={e => { const v = e.target.value; setMetodoGlobal(v); if (v !== "GIRA") setFilterGiraId(""); }}
              title="Filtrar todo el pipeline por método de entrega"
              style={{
                padding: "5px 8px", borderRadius: "8px",
                border: metodoGlobal !== "TODOS" ? "1.5px solid #93c5fd" : "1.5px solid #e2e8f0",
                background: metodoGlobal !== "TODOS" ? "#eff6ff" : "#f8fafc",
                color: "#0f172a", fontSize: "0.78rem",
                fontWeight: metodoGlobal !== "TODOS" ? 700 : 500, cursor: "pointer",
              }}
            >
              <option value="TODOS">Todos</option>
              <option value="RETIRA">🤝 Retira</option>
              <option value="AGENCIA">🏢 Agencia</option>
              <option value="GIRA">🚚 Gira</option>
              <option value="CAMION">🚛 Camión</option>
            </select>
            {metodoGlobal === "GIRA" && (
              <select
                value={filterGiraId}
                onChange={e => setFilterGiraId(e.target.value)}
                title="Elegí una gira puntual"
                style={{
                  padding: "5px 8px", borderRadius: "8px", border: "1.5px solid #bfdbfe",
                  background: "#eff6ff", color: "#1d4ed8", fontSize: "0.78rem",
                  fontWeight: 600, cursor: "pointer", maxWidth: "190px",
                }}
              >
                <option value="">Todas las giras</option>
                {giras.map(g => {
                  const f = g.fechaSalida?.seconds
                    ? ` · ${new Date(g.fechaSalida.seconds * 1000).toLocaleDateString("es-UY", { day: "2-digit", month: "short" })}`
                    : "";
                  return (
                    <option key={g.id} value={g.id}>
                      {(g.descripcion || "Gira")}{g.codigo ? ` (${g.codigo})` : ""}{f}
                    </option>
                  );
                })}
                {giras.length === 0 && <option value="__none" disabled>No hay giras activas</option>}
              </select>
            )}
          </div>
        )}

        {/* Chips de depósito */}
        <div style={{ display: "flex", gap: "5px", flexShrink: 0 }}>
          {["", ...deposits].map(dep => {
            const active = filterDeposito === dep;
            return (
              <button
                key={dep || "__todos__"}
                onClick={() => setFilterDeposito(dep)}
                style={{
                  padding: "4px 12px",
                  borderRadius: "999px",
                  border: "none",
                  background: active ? "#0f172a" : "#f1f5f9",
                  color: active ? "#fff" : "#64748b",
                  fontSize: "0.74rem",
                  fontWeight: active ? 700 : 500,
                  cursor: "pointer",
                  transition: "all 0.1s",
                }}
              >
                {dep || "Todos"}
              </button>
            );
          })}
        </div>

        {/* Búsqueda */}
        <input
          placeholder="🔍 Buscar cliente o #pedido…"
          value={searchQ}
          onChange={e => setSearchQ(e.target.value)}
          style={{
            padding: "6px 12px",
            borderRadius: "8px",
            border: "1.5px solid #e2e8f0",
            fontSize: "0.83rem",
            width: "210px",
            background: "#f8fafc",
            flexShrink: 0,
          }}
        />

        {/* Total pedidos */}
        {!loading && (
          <span style={{ fontSize: "0.72rem", color: "#94a3b8", whiteSpace: "nowrap", flexShrink: 0 }}>
            {totalPedidos} pedido{totalPedidos !== 1 ? "s" : ""}
          </span>
        )}

        {/* Botón sync Finnegans */}
        <button
          onClick={handleSync}
          disabled={syncing}
          title={lastSync
            ? `Última actualización: ${lastSync.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })}`
            : "Actualizar pedidos desde Finnegans"}
          style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            gap: "5px",
            height: "32px",
            padding: "0 12px",
            background: syncing ? "#f1f5f9" : "#0f172a",
            border: "none",
            borderRadius: "8px",
            color: syncing ? "#94a3b8" : "#fff",
            fontSize: "0.76rem",
            fontWeight: 700,
            cursor: syncing ? "not-allowed" : "pointer",
            flexShrink: 0,
            transition: "all 0.15s",
          }}
        >
          <span style={{
            display: "inline-block",
            animation: syncing ? "spin 0.8s linear infinite" : "none",
          }}>
            🔄
          </span>
          {syncing ? "Actualizando…" : "Actualizar"}
          {lastSync && !syncing && (
            <span style={{ fontSize: "0.65rem", fontWeight: 400, opacity: 0.7, marginLeft: "2px" }}>
              {lastSync.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
        </button>

        {/* Link al histórico de despachos */}
        <a
          href="/ventas/despachados"
          title="Histórico de despachos"
          style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            width: "32px", height: "32px",
            background: "#f8fafc", border: "1px solid #e2e8f0",
            borderRadius: "8px", textDecoration: "none",
            fontSize: "0.9rem", flexShrink: 0,
          }}
        >
          📋
        </a>

        {/* Link al histórico de entregados */}
        <a
          href="/ventas/entregados"
          title="Histórico de pedidos entregados"
          style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            gap: "5px",
            height: "32px",
            padding: "0 11px",
            background: "#f0fdf4", border: "1px solid #bbf7d0",
            borderRadius: "8px", textDecoration: "none",
            fontSize: "0.74rem", fontWeight: 700,
            color: "#15803d", flexShrink: 0,
            whiteSpace: "nowrap",
          }}
        >
          📦 Entregados
        </a>

        {/* Link al historial unificado de cargas y entregas */}
        <a
          href="/historial-cargas"
          title="Historial de cargas y entregas"
          style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            gap: "5px",
            height: "32px",
            padding: "0 11px",
            background: "#f0f9ff", border: "1px solid #bae6fd",
            borderRadius: "8px", textDecoration: "none",
            fontSize: "0.74rem", fontWeight: 700,
            color: "#0369a1", flexShrink: 0,
            whiteSpace: "nowrap",
          }}
        >
          🚛 Cargas
        </a>

        {/* Link a pedidos sin depósito con badge — solo si puede actuar */}
        {!soloLectura && <a
          href="/ventas/sin-deposito"
          title="Pedidos sin depósito asignado"
          style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            gap: "5px",
            height: "32px",
            padding: "0 11px",
            background: sinDeposito > 0 ? "#fff7ed" : "#f8fafc",
            border: `1px solid ${sinDeposito > 0 ? "#fdba74" : "#e2e8f0"}`,
            borderRadius: "8px", textDecoration: "none",
            fontSize: "0.74rem", fontWeight: 700,
            color: sinDeposito > 0 ? "#c2410c" : "#64748b",
            flexShrink: 0,
            whiteSpace: "nowrap",
          }}
        >
          🗂️ Sin clasificar
          {sinDeposito > 0 && (
            <span style={{
              background: "#f97316", color: "#fff",
              fontSize: "0.62rem", fontWeight: 800,
              padding: "1px 5px", borderRadius: "999px",
              lineHeight: 1.4,
            }}>
              {sinDeposito}
            </span>
          )}
        </a>}

        {/* Link a Pagos con badge de pendientes — SOLO ISABELA (R8 no carga pago) */}
        {!soloLectura && isIsabela && (
          <a
            href="/ventas/pagos"
            title="Pagos de pedidos despachados"
            style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              gap: "5px",
              height: "32px",
              padding: "0 11px",
              background: pagosPendientes > 0 ? "#fffbeb" : "#f8fafc",
              border: `1px solid ${pagosPendientes > 0 ? "#fde68a" : "#e2e8f0"}`,
              borderRadius: "8px", textDecoration: "none",
              fontSize: "0.74rem", fontWeight: 700,
              color: pagosPendientes > 0 ? "#b45309" : "#64748b",
              flexShrink: 0,
              whiteSpace: "nowrap",
              position: "relative",
            }}
          >
            💰 Pagos
            {pagosPendientes > 0 && (
              <span style={{
                background: "#ef4444", color: "#fff",
                fontSize: "0.62rem", fontWeight: 800,
                padding: "1px 5px", borderRadius: "999px",
                lineHeight: 1.4,
              }}>
                {pagosPendientes}
              </span>
            )}
          </a>
        )}
      </div>

      {/* ── Barra divisora (mobile): la preparación va sin filtro; el filtro Retiro/
           Transporte aplica de la ENTREGA en adelante. En desktop el corte va DENTRO
           del kanban (línea vertical + filtros entre "Para despachar" y "Despachado").
           Cuando el filtro GLOBAL de método está activo, este split manual se oculta
           (el dropdown de arriba ya está filtrando todo). ── */}
      {!isIsabela && !loading && isMobile && !metodoActivo && (
        <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", padding: "8px 16px", background: "#fff", borderBottom: "1px solid #e2e8f0" }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
            <span style={{ fontSize: "0.7rem", fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>Preparación</span>
            <span style={{ fontSize: "0.72rem", color: "#94a3b8" }}>· todos los métodos</span>
          </div>
          <div style={{ width: "1px", alignSelf: "stretch", minHeight: "22px", background: "#cbd5e1" }} />
          <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
            <span style={{ fontSize: "0.7rem", fontWeight: 800, color: "#0f172a", textTransform: "uppercase", letterSpacing: "0.05em" }}>Entrega:</span>
            {[{ id: "retiro", label: "🤝 Retiro" }, { id: "transporte", label: "🚚 Transporte" }].map(t => {
              const active = procesoTab === t.id;
              return (
                <button key={t.id} onClick={() => setProcesoTab(t.id)}
                  style={{ padding: "4px 12px", borderRadius: "999px", border: active ? "1.5px solid #0f172a" : "1.5px solid #e2e8f0", background: active ? "#0f172a" : "#fff", color: active ? "#fff" : "#475569", fontSize: "0.76rem", fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
                  {t.label}
                </button>
              );
            })}
            {procesoTab === "transporte" && (
              <div style={{ display: "flex", gap: "3px", marginLeft: "2px", borderLeft: "1px solid #e2e8f0", paddingLeft: "8px" }}>
                {[{ id: "todos", label: "Todos" }, { id: "AGENCIA", label: "Agencia" }, { id: "GIRA", label: "Gira" }, { id: "CAMION", label: "Camión" }].map(s => {
                  const active = transporteSub === s.id;
                  return (
                    <button key={s.id} onClick={() => setTransporteSub(s.id)}
                      style={{ padding: "3px 9px", borderRadius: "999px", border: "none", background: active ? "#1d4ed8" : "#eff6ff", color: active ? "#fff" : "#1d4ed8", fontSize: "0.72rem", fontWeight: active ? 700 : 500, cursor: "pointer", whiteSpace: "nowrap" }}>
                      {s.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Kanban / Vista mobile ── */}
      {loading ? (
        <div style={{
          flex: 1, display: "flex",
          alignItems: "center", justifyContent: "center",
          color: "#94a3b8", fontSize: "0.9rem",
        }}>
          Cargando pedidos…
        </div>
      ) : isMobile ? (
        /* ── Vista mobile: secciones verticales en orden de flujo ── */
        <div style={{ flex: 1, overflowY: "auto", padding: "12px 14px" }}>
          {pipelineUnits.map(unit => {
            if (unit.type === "single") return renderMobileSection(unit.cfg);
            // Grupo "Despachado": banner + dos sub-secciones apiladas
            const total = (byEstado[unit.cargar.id] || []).length + (byEstado[unit.entregar.id] || []).length;
            if (total === 0) return null;
            return (
              <div key="group-despachado" style={{ marginBottom: "16px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "6px", padding: "8px 10px", background: "#dbeafe", border: "1.5px solid #85b7eb", borderRadius: "10px", marginBottom: "8px" }}>
                  <span style={{ fontSize: "0.85rem", lineHeight: 1 }}>📦</span>
                  <span style={{ flex: 1, fontWeight: 700, fontSize: "0.8rem", color: "#185fa5" }}>Despachado</span>
                  <span style={{ background: "#185fa522", color: "#185fa5", fontSize: "0.7rem", fontWeight: 700, padding: "1px 8px", borderRadius: "999px" }}>{total}</span>
                </div>
                <div style={{ paddingLeft: "8px", borderLeft: "2px solid #bae6fd" }}>
                  {renderMobileSection(unit.cargar, { labelOverride: "Cargar camión", forceShow: true })}
                  {renderMobileSection(unit.entregar, { labelOverride: "Entregar", forceShow: true })}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* ── Vista desktop: kanban horizontal ── */
        <div style={{
          flex: 1,
          display: "flex",
          gap: "10px",
          padding: "10px 16px",
          overflowX: "auto",
          alignItems: "stretch",
          boxSizing: "border-box",
        }}>
          {(() => {
            // Línea divisoria vertical + filtros de ENTREGA: se insertan ANTES de la
            // primera columna post-despacho, para dejar claro que el filtro Retiro/
            // Transporte solo afecta de "Despachado" en adelante (no la preparación).
            const POST = ["DESPACHADO", "DESPACHADO_CARGA", "EN_CAMION", "ENTREGADO"];
            let dividerDone = false;
            // Etiqueta del foco activo (cuando el filtro GLOBAL está prendido).
            const metodoLabel = { RETIRA: "🤝 Retira", AGENCIA: "🏢 Agencia", GIRA: "🚚 Gira", CAMION: "🚛 Camión" }[metodoGlobal] || "";
            const giraSel = metodoGlobal === "GIRA" && filterGiraId ? giras.find(g => g.id === filterGiraId) : null;
            const divider = (
              <div key="__divfiltros" style={{ flexShrink: 0, alignSelf: "stretch", display: "flex", flexDirection: "column", gap: "8px", paddingLeft: "12px", marginLeft: "2px", borderLeft: "2px solid #cbd5e1" }}>
                <span style={{ fontSize: "0.62rem", fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>Entrega</span>
                {metodoActivo ? (
                  // El filtro global manda: mostramos el foco activo (solo lectura); el
                  // usuario lo cambia desde el dropdown de arriba.
                  <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                    <span style={{ padding: "5px 10px", borderRadius: "999px", background: "#eff6ff", border: "1.5px solid #bfdbfe", color: "#1d4ed8", fontSize: "0.72rem", fontWeight: 700, whiteSpace: "nowrap", textAlign: "center" }}>
                      {metodoLabel}
                    </span>
                    {giraSel && (
                      <span style={{ fontSize: "0.64rem", color: "#64748b", textAlign: "center", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "120px" }}>
                        {giraSel.descripcion || "Gira"}{giraSel.codigo ? ` (${giraSel.codigo})` : ""}
                      </span>
                    )}
                  </div>
                ) : (
                  <>
                    <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                      {[{ id: "retiro", label: "🤝 Retiro" }, { id: "transporte", label: "🚚 Transporte" }].map(t => {
                        const active = procesoTab === t.id;
                        return (
                          <button key={t.id} onClick={() => setProcesoTab(t.id)}
                            style={{ padding: "5px 10px", borderRadius: "999px", border: active ? "1.5px solid #0f172a" : "1.5px solid #e2e8f0", background: active ? "#0f172a" : "#fff", color: active ? "#fff" : "#475569", fontSize: "0.74rem", fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
                            {t.label}
                          </button>
                        );
                      })}
                    </div>
                    {procesoTab === "transporte" && (
                      <div style={{ display: "flex", flexDirection: "column", gap: "4px", borderTop: "1px solid #e2e8f0", paddingTop: "6px" }}>
                        {[{ id: "todos", label: "Todos" }, { id: "AGENCIA", label: "Agencia" }, { id: "GIRA", label: "Gira" }, { id: "CAMION", label: "Camión" }].map(s => {
                          const active = transporteSub === s.id;
                          return (
                            <button key={s.id} onClick={() => setTransporteSub(s.id)}
                              style={{ padding: "3px 8px", borderRadius: "999px", border: "none", background: active ? "#1d4ed8" : "#eff6ff", color: active ? "#fff" : "#1d4ed8", fontSize: "0.68rem", fontWeight: active ? 700 : 500, cursor: "pointer", whiteSpace: "nowrap" }}>
                              {s.label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
            );

            return pipelineUnits.flatMap(unit => {
              const id = unit.type === "single" ? unit.cfg.id : "group";
              const isPost = unit.type === "group" || POST.includes(id);
              const pre = [];
              if (isPost && !dividerDone && !isIsabela) { dividerDone = true; pre.push(divider); }

              if (unit.type === "single") {
                return [...pre, (
                  <div key={unit.cfg.id} style={{ flex: "1 1 0", minWidth: "165px" }}>
                    <PipelineColumn
                      cfg={unit.cfg}
                      items={byEstado[unit.cfg.id] || []}
                      colHeight={colHeight}
                      onReasignar={puedeReasignarDeposito ? setReasignando : null}
                      pagosSet={pagosSet}
                      onDespachar={onDespacharAtajo}
                      onFacturar={unit.cfg.id === "POR_FACTURAR" ? onFacturar : null}
                    />
                  </div>
                )];
              }
              // Grupo "Despachado" con dos sub-columnas
              const cargarItems = byEstado[unit.cargar.id] || [];
              const entregarItems = byEstado[unit.entregar.id] || [];
              const total = cargarItems.length + entregarItems.length;
              return [...pre, (
              <div key="group-despachado" style={{ flex: "2 1 0", minWidth: "340px", display: "flex", flexDirection: "column", height: colHeight }}>
                {/* Banner Despachado */}
                <div style={{ background: "#dbeafe", border: "1.5px solid #85b7eb", borderBottom: "none", borderRadius: "14px 14px 0 0", padding: "10px 14px", display: "flex", alignItems: "center", gap: "6px", flexShrink: 0 }}>
                  <span style={{ fontSize: "0.88rem", lineHeight: 1 }}>📦</span>
                  <span style={{ flex: 1, fontWeight: 700, fontSize: "0.82rem", color: "#185fa5" }}>Despachado</span>
                  <span style={{ background: total > 0 ? "#185fa522" : "#f1f5f9", color: total > 0 ? "#185fa5" : "#94a3b8", fontSize: "0.72rem", fontWeight: 700, padding: "1px 8px", borderRadius: "999px", minWidth: "22px", textAlign: "center" }}>{total}</span>
                </div>
                {/* Dos sub-columnas */}
                <div style={{ flex: 1, minHeight: 0, display: "flex", gap: "8px", padding: "8px", border: "1.5px solid #85b7eb", borderTop: "none", borderRadius: "0 0 14px 14px", background: "#f4f9fe" }}>
                  <div style={{ flex: "1 1 0", minWidth: 0 }}>
                    <PipelineColumn
                      cfg={unit.cargar}
                      items={cargarItems}
                      colHeight="100%"
                      labelOverride="Cargar camión"
                      groupedInner
                      onReasignar={puedeReasignarDeposito ? setReasignando : null}
                      pagosSet={pagosSet}
                    />
                  </div>
                  <div style={{ flex: "1 1 0", minWidth: 0 }}>
                    <PipelineColumn
                      cfg={unit.entregar}
                      items={entregarItems}
                      colHeight="100%"
                      labelOverride="Entregar"
                      groupedInner
                      onReasignar={puedeReasignarDeposito ? setReasignando : null}
                      pagosSet={pagosSet}
                    />
                  </div>
                </div>
              </div>
            )];
            });
          })()}
        </div>
      )}

      {/* Modal reasignar depósito */}
      {reasignando && (
        <AsignarDepositoModal
          pedido={reasignando}
          titulo="Reasignar depósito"
          onClose={() => setReasignando(null)}
          onSaved={() => setReasignando(null)}
        />
      )}

      {/* Atajo de despacho: valida método + agencia/viaje sin entrar al detalle */}
      {despachando && (
        <div
          onClick={() => setDespachando(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: "#fff", borderRadius: "16px", width: "100%", maxWidth: "460px", maxHeight: "90vh", overflowY: "auto", padding: "20px", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
              <div style={{ minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: "0.7rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>🚚 Despachar</p>
                <h3 style={{ margin: "2px 0 0", fontSize: "1.05rem", fontWeight: 800, color: "#0f172a" }}>#{despachando.numero || despachando.id}</h3>
                {despachando.cliente && <p style={{ margin: "2px 0 0", fontSize: "0.85rem", color: "#475569", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{despachando.cliente}</p>}
              </div>
              <button onClick={() => setDespachando(null)} style={{ background: "none", border: "none", fontSize: "22px", cursor: "pointer", color: "#94a3b8", lineHeight: 1 }}>×</button>
            </div>
            <ConfirmarDespacho pedidoId={despachando.id} compact onDone={() => setDespachando(null)} />
          </div>
        </div>
      )}
    </div>
  );
}
